'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Music2 } from 'lucide-react';

const playlist = [
  { id: 'track-crazy-train', title: 'Crazy Train', artist: 'Ozzy Osbourne', src: '/assets/audio/crazy-train.mp3' },
  { id: 'track-uptown-funk', title: 'Uptown Funk', artist: 'Bruno Mars ft. Mark Ronson', src: '/assets/audio/uptown-funk.mp3' },
];

export default function BackgroundMusicPlayer() {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.4);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!audioRef?.current) return;
    audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const togglePlay = () => {
    if (!audioRef?.current) return;
    if (playing) {
      audioRef?.current?.pause();
    } else {
      audioRef?.current?.play()?.catch(() => {});
    }
    setPlaying(!playing);
  };

  const handleTrackEnd = () => {
    const next = (currentTrackIndex + 1) % playlist?.length;
    setCurrentTrackIndex(next);
    setPlaying(false);
    setTimeout(() => {
      if (audioRef?.current) {
        audioRef?.current?.load();
        audioRef?.current?.play()?.catch(() => {});
        setPlaying(true);
      }
    }, 100);
  };

  const currentTrack = playlist?.[currentTrackIndex];

  return (
    <div className="fixed bottom-24 left-4 z-40">
      {/* Audio element — backend: replace src with CDN or streaming URLs */}
      <audio
        ref={audioRef}
        src={currentTrack?.src}
        onEnded={handleTrackEnd}
       
      />
      <div className={`rounded-2xl card-glass neon-border-gold transition-all duration-300 overflow-hidden ${expanded ? 'w-64' : 'w-auto'}`}>
        {/* Compact bar */}
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            onClick={togglePlay}
            className="w-8 h-8 rounded-full gold-gradient flex items-center justify-center hover:opacity-90 active:scale-95 transition-all shrink-0"
            aria-label={playing ? 'Pause music' : 'Play music'}
          >
            {playing ? <Pause size={13} className="text-black" /> : <Play size={13} className="text-black ml-0.5" />}
          </button>

          {expanded && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">{currentTrack?.title}</p>
              <p className="text-xs text-muted-foreground truncate">{currentTrack?.artist}</p>
            </div>
          )}

          <button
            onClick={() => setMuted(!muted)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary transition-colors shrink-0"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
          </button>

          <button
            onClick={() => setExpanded(!expanded)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary transition-colors shrink-0"
            aria-label="Toggle player"
          >
            <Music2 size={13} />
          </button>
        </div>

        {/* Expanded controls */}
        {expanded && (
          <div className="px-3 pb-3 border-t border-border pt-2">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(parseFloat(e?.target?.value)); setMuted(false); }}
              onPointerDown={(e) => { try { (e.currentTarget as HTMLInputElement).setPointerCapture(e.pointerId); } catch {} }}
              onPointerUp={(e) => { try { (e.currentTarget as HTMLInputElement).releasePointerCapture(e.pointerId); } catch {} }}
              className="w-full h-1 rounded-full accent-primary cursor-pointer"
              aria-label="Volume"
            />
            <div className="flex justify-between mt-2">
              {playlist?.map((track, i) => (
                <button
                  key={track?.id}
                  onClick={() => {
                    setCurrentTrackIndex(i);
                    setPlaying(false);
                    setTimeout(() => {
                      if (audioRef?.current) {
                        audioRef?.current?.load();
                        audioRef?.current?.play()?.catch(() => {});
                        setPlaying(true);
                      }
                    }, 100);
                  }}
                  className={`text-xs px-2 py-1 rounded-lg transition-colors ${
                    currentTrackIndex === i ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {i + 1}. {track?.title?.split(' ')?.slice(0, 2)?.join(' ')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}